// MarkdownRenderer.tsx
'use client';

import {
  Box,
  Link as CLink,
  Code,
  Heading,
  TableBody,
  TableCell,
  TableHeader,
  TableRoot,
  TableRow,
  Text,
} from '@chakra-ui/react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';

import type { Components } from 'react-markdown';

import './style/MarkdownRender.css';

const components: Components = {
  h1: (p) => <Heading as="h1" size="xl" mt={6} mb={3} {...p} />,
  h2: (p) => <Heading as="h2" size="lg" mt={5} mb={2} {...p} />,
  h3: (p) => <Heading as="h3" size="md" mt={4} mb={2} {...p} />,
  p:  (p) => <Text mb={3} {...p} />,
  a:  ({href, children, ...rest}) => (
    <CLink href={href} rel="nofollow noopener noreferrer" {...rest} target="_blank">
      {children}
    </CLink>
  ),
  em: ({ children, ...rest }) => <em style={{ fontStyle: 'italic' }} {...rest}>{children}</em>,
  strong: ({ children, ...rest }) => <strong style={{ fontWeight: 'semibold' }} {...rest}>{children}</strong>,
  ul: ({ children, ...rest }) => <ul style={{ paddingLeft: '24px', marginBottom: '12px' }} {...rest}>{children}</ul>,
  ol: ({ children, ...rest }) => <ol style={{ paddingLeft: '24px', marginBottom: '12px' }} {...rest}>{children}</ol>,
  li: ({ children, ...rest }) => <li style={{ marginBottom: '4px' }} {...rest}>{children}</li>,
  pre: ({ children, ...rest }) => (
    <pre {...rest}>
      {children}
    </pre>
  ),
  code: ({inline, children, ...rest}: {inline?: boolean, children: React.ReactNode} & any) =>
    inline ? (
      <Code {...rest}>{children}</Code>
    ) : (
      <Code whiteSpace="pre" w="full" display="block" {...rest}>{children}</Code>
    ),
  table: ({children}) => (
    <Box overflowX="auto" my={4}>
      <TableRoot size="sm">{children}</TableRoot>
    </Box>
  ),
  thead: TableHeader,
  tbody: TableBody,
  tr: TableRow,
  th: (p) => <TableCell fontWeight="semibold" {...p} />,
  td: (p) => <TableCell {...p} />,
};
const MarkdownRenderer = ({ content }: { content: string }) => {
  return (
    <Box className="markdown-renderer">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeSanitize]}
        components={components}
      >
        {content}
      </ReactMarkdown>
    </Box>
  );
};

export default MarkdownRenderer;
